/**
 * Firebase utility functions module.
 * Contains shared utility functions used across Firebase modules.
 *
 * @module firebase/utils
 */

/**
 * Timing constants for session management
 * @type {Object}
 */
export const SESSION_TIMING = {
  AWAY_TIMEOUT: 2 * 60 * 1000, // 2 minutes
  OFFLINE_TIMEOUT: 5 * 60 * 1000, // 5 minutes
  HEARTBEAT_INTERVAL: 60 * 1000, // 1 minute
  VERIFICATION_DELAY: 5000, // 5 seconds
};

/**
 * Parses Firebase timestamp to JavaScript timestamp in milliseconds
 *
 * @param {Object|number} timestamp - Firebase timestamp object or number
 * @returns {number} JavaScript timestamp (milliseconds since epoch)
 */
export const parseFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return Date.now();
  if (typeof timestamp === "number") return timestamp;
  if (typeof timestamp === "object") {
    if (timestamp.seconds) return timestamp.seconds * 1000;
    if (timestamp._seconds) return timestamp._seconds * 1000;
    if (timestamp.toMillis && typeof timestamp.toMillis === "function") {
      return timestamp.toMillis();
    }
  }
  return Date.now();
};

/**
 * Calculates session status based on inactive time
 *
 * @param {number} inactiveTime - Time in milliseconds since last activity
 * @returns {string} Status: 'online', 'away', or 'offline'
 */
export const calculateSessionStatus = (inactiveTime) => {
  if (inactiveTime < SESSION_TIMING.AWAY_TIMEOUT) {
    return "online";
  } else if (inactiveTime < SESSION_TIMING.OFFLINE_TIMEOUT) {
    return "away";
  } else {
    return "offline";
  }
};

/**
 * Generates a unique connection ID for session tracking
 *
 * @returns {string} Unique connection ID
 */
export const generateConnectionId = () => {
  try {
    const existingId = sessionStorage.getItem("adminConnectionId");
    if (existingId) {
      return existingId;
    }
    const newId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("adminConnectionId", newId);
    return newId;
  } catch (e) {
    console.warn("Could not use sessionStorage for connection ID:", e);
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

/**
 * Safely clears multiple timers
 *
 * @param {...(number|null)} timers - Timers to clear
 */
export const clearTimers = (...timers) => {
  timers.forEach((timer) => {
    if (timer) clearTimeout(timer);
  });
};
